/**
 * 指挥页救援搜索：临时水滴 Marker（WGS84 落点，显示坐标经 toMapLngLat）
 * PITFALLS 6.1：根节点勿写 position/transform；复用 createRescueIcon。
 */
import mapboxgl from 'mapbox-gl';
import { createRescueIcon } from './rescueMarkers';
import { toMapLngLat } from './basemapCoord';
import { parseCoordinateString } from '../coordParse';

export function clearRescueSearchTempMarker(holder) {
  if (!holder) return;
  try {
    holder.popup?.remove?.();
  } catch (_) {
    /* ignore */
  }
  try {
    holder.marker?.remove?.();
  } catch (_) {
    /* ignore */
  }
  holder.marker = null;
  holder.popup = null;
  holder.wgs = null;
  holder._onSet = null;
}

/**
 * @param {mapboxgl.Map} map
 * @param {number} lat WGS84
 * @param {number} lng WGS84
 * @param {{
 *   holder: { marker?: any, popup?: any, wgs?: any, _onSet?: any },
 *   onSetAsTarget?: (lat: number, lng: number) => void,
 *   fly?: boolean,
 *   title?: string
 * }} options
 */
export function placeRescueSearchTempMarker(map, lat, lng, options = {}) {
  const holder = options.holder || {};
  const onSetAsTarget = options.onSetAsTarget;
  const fly = options.fly !== false;
  const title = options.title || '搜索坐标';

  if (!map || !Number.isFinite(Number(lat)) || !Number.isFinite(Number(lng))) {
    return null;
  }

  const la = Number(lat);
  const ln = Number(lng);
  clearRescueSearchTempMarker(holder);

  try {
    if (map.getProjection?.()?.name === 'globe' && typeof map.setProjection === 'function') {
      map.setProjection('mercator');
    }
  } catch (_) {
    /* ignore */
  }

  const display = toMapLngLat(ln, la, undefined, map) || [ln, la];
  const el = createRescueIcon('#f59e0b', title);
  el.title = `${title}\n${la.toFixed(6)}, ${ln.toFixed(6)}`;

  const popup = new mapboxgl.Popup({
    offset: 28,
    closeButton: true,
    closeOnClick: false,
    maxWidth: '240px',
    className: 'zhxt-rescue-search-temp-popup'
  }).setHTML(`
    <div style="font-size:12px;min-width:160px;line-height:1.4">
      <div style="font-weight:600;margin-bottom:4px">临时坐标</div>
      <div style="font-family:ui-monospace,Menlo,Consolas,monospace;font-size:11px;color:#334155;margin-bottom:8px">
        ${la.toFixed(6)}, ${ln.toFixed(6)}
      </div>
      <button
        type="button"
        data-zhxt-rescue-search-set-target="1"
        style="width:100%;padding:6px 10px;font-size:12px;font-weight:600;border:none;border-radius:4px;background:#1976d2;color:#fff;cursor:pointer"
      >设置为目标位置</button>
    </div>
  `);

  const marker = new mapboxgl.Marker({
    element: el,
    anchor: 'bottom',
    pitchAlignment: 'viewport',
    rotationAlignment: 'viewport'
  })
    .setLngLat(display)
    .setPopup(popup)
    .addTo(map);

  const bindPopupClick = () => {
    const root = popup.getElement();
    if (!root) return;
    const btn = root.querySelector('[data-zhxt-rescue-search-set-target]');
    if (!btn || btn.__zhxtBound) return;
    btn.__zhxtBound = true;
    btn.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      try {
        onSetAsTarget?.(la, ln);
      } finally {
        clearRescueSearchTempMarker(holder);
      }
    });
  };

  popup.on('open', bindPopupClick);
  el.addEventListener('click', (event) => {
    event.stopPropagation();
    if (!popup.isOpen()) marker.togglePopup();
  });

  if (fly) {
    try {
      map.stop?.();
    } catch (_) {
      /* ignore */
    }
    map.flyTo({
      center: display,
      zoom: 16.7,
      duration: 2000,
      essential: true
    });
  }

  holder.marker = marker;
  holder.popup = popup;
  holder.wgs = { lat: la, lng: ln };
  holder._onSet = onSetAsTarget;
  // 默认弹出一次，方便直接点「设为目标」
  try {
    marker.togglePopup();
  } catch (_) {
    /* ignore */
  }
  return holder;
}

/**
 * @returns {Array<{
 *   key: string,
 *   kind: 'coord'|'target'|'track',
 *   lat: number,
 *   lng: number,
 *   label: string,
 *   sub?: string,
 *   placeTemp?: boolean
 * }>}
 */


/**
 * 清除搜索「待推送」正式水滴（设为目标后、推送成功后、关弹层时可调）
 */
export function clearRescueSearchDraftTargetMarker(holder) {
  if (!holder) return;
  try {
    holder.popup?.remove?.();
  } catch (_) {
    /* ignore */
  }
  try {
    holder.marker?.remove?.();
  } catch (_) {
    /* ignore */
  }
  holder.marker = null;
  holder.popup = null;
  holder.wgs = null;
  holder._onOpenPush = null;
}

/**
 * 搜索坐标「设置为目标位置」：在地图上打持久水滴（非临时点）。
 * 点击水滴 → 打开与「目标推送」同一套推送 UI（由 onOpenPush 挂接）。
 * PITFALLS 6.1：根节点勿写 position/transform；复用 createRescueIcon。
 *
 * @param {mapboxgl.Map} map
 * @param {number} lat WGS84
 * @param {number} lng WGS84
 * @param {{
 *   holder: { marker?: any, popup?: any, wgs?: any, _onOpenPush?: any },
 *   onOpenPush?: (lat: number, lng: number) => void,
 *   fly?: boolean,
 *   title?: string
 * }} options
 */
export function placeRescueSearchDraftTargetMarker(map, lat, lng, options = {}) {
  const holder = options.holder || {};
  const onOpenPush = options.onOpenPush;
  const fly = options.fly !== false;
  const title = options.title || '待推送目标';

  if (!map || !Number.isFinite(Number(lat)) || !Number.isFinite(Number(lng))) {
    return null;
  }

  const la = Number(lat);
  const ln = Number(lng);
  clearRescueSearchDraftTargetMarker(holder);

  try {
    if (map.getProjection?.()?.name === 'globe' && typeof map.setProjection === 'function') {
      map.setProjection('mercator');
    }
  } catch (_) {
    /* ignore */
  }

  const display = toMapLngLat(ln, la, undefined, map) || [ln, la];
  // 与指挥端救援目标普通级别同色，正式打点观感
  const el = createRescueIcon('#1976d2', title, { badge: '' });
  el.title = `${title}\n${la.toFixed(6)}, ${ln.toFixed(6)}\n点击可推送给队员`;
  el.setAttribute('data-zhxt-rescue-search-draft', '1');

  const popup = new mapboxgl.Popup({
    offset: 28,
    closeButton: true,
    closeOnClick: false,
    maxWidth: '260px',
    className: 'zhxt-rescue-search-draft-popup'
  }).setHTML(`
    <div style="font-size:12px;min-width:170px;line-height:1.4">
      <div style="font-weight:600;margin-bottom:4px;color:#0d47a1">目标位置（待推送）</div>
      <div style="font-family:ui-monospace,Menlo,Consolas,monospace;font-size:11px;color:#334155;margin-bottom:8px">
        ${la.toFixed(6)}, ${ln.toFixed(6)}
      </div>
      <button
        type="button"
        data-zhxt-rescue-search-open-push="1"
        style="width:100%;padding:6px 10px;font-size:12px;font-weight:600;border:none;border-radius:4px;background:#1976d2;color:#fff;cursor:pointer"
      >推送给队员</button>
    </div>
  `);

  const marker = new mapboxgl.Marker({
    element: el,
    anchor: 'bottom',
    pitchAlignment: 'viewport',
    rotationAlignment: 'viewport'
  })
    .setLngLat(display)
    .setPopup(popup)
    .addTo(map);

  const bindPopupClick = () => {
    const root = popup.getElement();
    if (!root) return;
    const btn = root.querySelector('[data-zhxt-rescue-search-open-push]');
    if (!btn || btn.__zhxtBound) return;
    btn.__zhxtBound = true;
    btn.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      onOpenPush?.(la, ln);
    });
  };

  popup.on('open', bindPopupClick);
  el.addEventListener('click', (event) => {
    event.stopPropagation();
    if (!popup.isOpen()) marker.togglePopup();
  });

  if (fly) {
    try {
      map.stop?.();
    } catch (_) {
      /* ignore */
    }
    map.flyTo({
      center: display,
      zoom: 16.7,
      duration: 1600,
      essential: true
    });
  }

  holder.marker = marker;
  holder.popup = popup;
  holder.wgs = { lat: la, lng: ln };
  holder._onOpenPush = onOpenPush;
  try {
    marker.togglePopup();
  } catch (_) {
    /* ignore */
  }
  return holder;
}

export function buildRescueSearchResults(query, rescueTargets = [], rescueTracks = []) {
  const raw = String(query || '').trim();
  const results = [];
  const coords = parseCoordinateString(raw);
  if (coords) {
    results.push({
      key: `coord:${coords.lat},${coords.lng}`,
      kind: 'coord',
      lat: coords.lat,
      lng: coords.lng,
      label: `飞到坐标 ${coords.lat.toFixed(6)}, ${coords.lng.toFixed(6)}`,
      sub: '将放置临时水滴；设为目标后可推送给队员',
      placeTemp: true
    });
  }

  const q = raw.toLowerCase();
  if (!q) return results;

  // 纯坐标输入时仍可附带名称命中；空查询不列全量
  const looksOnlyCoord = !!coords && !/[a-zA-Z\u4e00-\u9fff]/.test(raw);
  if (looksOnlyCoord) return results;

  (rescueTargets || []).forEach((target) => {
    const name = String(target?.message || '');
    const desc = String(target?.description || '');
    const hay = `${name} ${desc}`.toLowerCase();
    if (!hay.includes(q)) return;
    const lat = Number(target?.lat);
    const lng = Number(target?.lng);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
    results.push({
      key: `target:${target?.id ?? `${lat},${lng}`}`,
      kind: 'target',
      lat,
      lng,
      label: name || '救援目标',
      sub: desc || (target?.level ? `级别: ${target.level}` : '救援目标'),
      placeTemp: false
    });
  });

  (rescueTracks || []).forEach((track) => {
    const name = String(track?.message || '');
    if (!name.toLowerCase().includes(q)) return;
    const start = Array.isArray(track?.coordinates) && track.coordinates.length
      ? track.coordinates[0]
      : null;
    const lat = Number(start?.lat);
    const lng = Number(start?.lng);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
    results.push({
      key: `track:${track?.id ?? `${lat},${lng}`}`,
      kind: 'track',
      lat,
      lng,
      label: name || '救援轨迹',
      sub: `轨迹 ${track.coordinates?.length || 0} 点`,
      placeTemp: false
    });
  });

  return results;
}

export function flyMapToRescueSearchWgs(map, lat, lng) {
  if (!map) return;
  const la = Number(lat);
  const ln = Number(lng);
  if (!Number.isFinite(la) || !Number.isFinite(ln)) return;
  try {
    if (map.getProjection?.()?.name === 'globe' && typeof map.setProjection === 'function') {
      map.setProjection('mercator');
    }
  } catch (_) {
    /* ignore */
  }
  const center = toMapLngLat(ln, la, undefined, map) || [ln, la];
  try {
    map.stop?.();
  } catch (_) {
    /* ignore */
  }
  map.flyTo({ center, zoom: 16.7, duration: 2000, essential: true });
}
